export interface BankInformation {
  bankTypeLabel: string;
  bankType: string;
  bankName: string;
  branchName: string;
  branchCode: string;
  accountNumber: number;
  accountSymbol: string;
  accountName: string;
}

export type ManageBankInformationPayload = Omit<BankInformation, 'bankTypeLabel'>;

export interface PaymentOverviewData {
  totalHoslogBalance: number;
  totalReceiveAmount: number;
  totalStoreAmount: number;
  totalAffiliateAmount: number;
}

export interface PaymentOverviewAnalyticsParams {
  fromDate: number;
  toDate: number;
}

export interface PaymentSalesBreakdownParams {
  year: number;
  row: number;
  fetch: number;
}

export interface PaymentOverviewAnalyticsPlan {
  planName: string;
  count: number;
}

export interface PaymentOverviewAnalyticsItem {
  date: number;
  plans: PaymentOverviewAnalyticsPlan[];
  total: number;
}

export interface PaymentOverviewPlanSummary {
  planName: string;
  planNameEng: string;
  percentage: number;
}

export interface PaymentOverviewAnalyticsData {
  analytics: PaymentOverviewAnalyticsItem[];
  planSummary: PaymentOverviewPlanSummary[];
}

export interface PaymentOverviewReservationLocation {
  locationId: string;
  locationName: string;
  locationNameEng: string;
  totalReservation: number;
}

export interface PaymentOverviewReservationChartData {
  totalReservations: number;
  locations: PaymentOverviewReservationLocation[];
}

export interface StorePaymentLogsParams {
  clubFilter?: string;
  locationFilter?: string;
  row?: number;
  fetch?: number;
  search?: string;
  fromDateMs?: number;
  toDateMs?: number;
}

export interface StorePaymentLogsData {
  clubId: string;
  id?: string;
  sn?: string | number;
  storeName?: string;
  storeInfo?: string;
  storeNameJp?: string;
  storeLogo?: {
    alt: string;
    description?: string;
    path: string;
  } | null;
  prefecture: string;
  createdDate?: number;
  totalBookings: number;
  visitDateTime: number;
  totalPlanAmount: number;
  commissionAmount: number;
  commisionAmount?: number;
  totalAmount: number;
}

export interface StorePaymentLogsDetailsParams {
  clubId: string;
  planTypeFilter?: string;
  paymentMethodFilter?: string;
  row?: number;
  fetch?: number;
  search?: string;
  fromDateMs?: number;
  toDateMs?: number;
}

export interface StorePaymentLogsDetailsData {
  sn?: string;
  id?: string;
  customerId?: string;
  customer: string;
  plan: string;
  noOfPeople?: number;
  NumberOfPeople?: number;
  visitDateTime: number;
  endDateTime?: number;
  createdDate?: number;
  paymentMethod: string;
  clubVerification: string;
  transactionStatus: string;
  reservationType: string;
  planAmount: number;
  totalPlanAmount: number;
  totalClubPlanAmount: number;
  adminPlanCommissionAmount: number;
  totalAdminPlanCommissionAmount: number;
  adminCommissionAmount: number;
  totalAdminCommissionAmount: number;
  totalAdminPayments: number;
}

export type StoreData = StorePaymentLogsDetailsData;

export interface AffiliateData {
  paymentId: string;
  monthNumber: number;
  /** Arrives with the next API deploy; until then the table derives it from the selected date range. */
  year?: number;
  /** English month name from the API (e.g. "October"); the table formats the month from `monthNumber`. */
  monthName: string;
  totalWithdrawCount: number;
  totalRequestedAmount: number;
  /** Day of the month the withdrawal is paid (e.g. 6). */
  withdrawDate: number;
}

export interface AffiliateWithdrawLogParams {
  /** Unix timestamp in milliseconds. */
  fromDate?: number;
  /** Unix timestamp in milliseconds. */
  toDate?: number;
  row?: number;
  fetch?: number;
}

export interface AffiliateWithdrawLogDetailParams {
  /** Unix timestamp in milliseconds. */
  fromDate?: number;
  /** Unix timestamp in milliseconds. */
  toDate?: number;
  search?: string;
  status?: AffiliateWithdrawStatus;
  row?: number;
  fetch?: number;
}

/** Status codes the withdraw-log detail API returns (same codes as the affiliate app). */
export enum AffiliateWithdrawStatus {
  PENDING = 'P',
  APPROVED = 'A',
  REJECTED = 'R',
}

export interface MonthlyReportData {
  requestId: string;
  /** Unix timestamp in milliseconds. */
  requestDate: number;
  bankType: string;
  bankName: string;
  /** In the design but not yet sent by the API. */
  accountSymbol?: string;
  accountNumber: string;
  accountName: string;
  requestedAmount: number;
  status: AffiliateWithdrawStatus;
  remarks: string;
}

export interface SalesBreakdownData {
  month: number;
  monthName: string;
  numberOfCustomer: number;
  numberOfStore: number;
  numberOfAffiliate: number;
  receiveAmount: number;
}
